import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    FiAlertCircle,
    FiCheckCircle,
    FiDollarSign,
    FiExternalLink,
    FiSave,
    FiX
} from 'react-icons/fi';
import { Home, PanelsTopLeft } from 'lucide-react';
import { HiOutlineSquare3Stack3D } from 'react-icons/hi2';

import { useAuth } from '../../contexts/AuthContext';
import BackButton from '../common/BackButton';
import Toast from '../common/Toast';
import {
    FINAL_PRICING_DEPARTMENTS,
    getFinalPricingJob,
    saveFinalPricing,
} from '../../lib/supabase/final-pricings';
import {
    isFinalPricingRequired,
    computeEstimationTotals,
} from '../../lib/supabase/finalPricingModes';
import { notifyReadyForQuotation } from '../../lib/supabase/pricingNotifications';

const VIEW_PERMISSION = 'view_finalpricing_page';
const EDIT_PERMISSION = 'edit_finalpricing_page';

const hasExactPermission = (userProfile, permission) =>
    Array.isArray(userProfile?.permissions) &&
    userProfile.permissions.includes(permission);

const DEPARTMENT_ICONS = {
    roof: Home,
    wall: PanelsTopLeft,
    floor: HiOutlineSquare3Stack3D,
};

const MONEY_FIELDS = [
    ['roof', 'Roof'],
    ['labour', 'Labour'],
    ['first_floor', 'First Floor'],
    ['second_floor', 'Second Floor'],
    ['gf_wall', 'Ground Floor Wall'],
    ['first_floor_wall', 'First Floor Wall'],
    ['second_floor_wall', 'Second Floor Wall'],
    ['beams', 'Beams'],
    ['flooring_sheets', 'Flooring Sheets'],
    ['approved_roof', 'Approved Roof'],
    ['approved_wall', 'Approved Wall'],
    ['approved_floor', 'Approved Floor'],
];

const formatMoney = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '—';

    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
    }).format(amount);
};

const parseMoney = (value) => {
    const normalized = String(value || '').replace(/[^0-9.-]/g, '');
    const amount = Number(normalized);
    return Number.isFinite(amount) ? amount : null;
};

const addressToText = (address) => {
    if (!address) return '—';
    if (typeof address === 'string') return address;

    return [
        address.street,
        address.street_address,
        address.address_line_1,
        address.suburb,
        address.city,
        address.state,
        address.postcode,
    ].filter(Boolean).join(', ') || '—';
};

const getEntryValues = (entry) => {
    const standard = MONEY_FIELDS
        .map(([key, label]) => ({ key, label, value: entry[key] }))
        .filter(item => item.value !== null && item.value !== undefined);

    const custom = Object.entries(entry.custom_prices || {})
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => ({
            key: `custom-${key}`,
            label: entry.custom_price_labels?.[key] || key,
            value,
        }));

    return [...standard, ...custom];
};

const PermissionDenied = ({ isModal = false, onClose }) => (
    <div
        className={
            isModal
                ? 'min-h-[420px] flex items-center justify-center p-6'
                : 'min-h-[55vh] flex items-center justify-center'
        }
    >
        <div className="max-w-md w-full rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
            <FiAlertCircle className="w-10 h-10 text-red-600 mx-auto mb-4" />

            <h1 className="text-xl font-semibold text-red-800">
                No permission
            </h1>

            <p className="text-sm text-red-700 mt-2">
                You do not have permission to view final pricing information.
            </p>

            {isModal && (
                <button
                    type="button"
                    onClick={onClose}
                    className="mt-5 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700"
                >
                    Close
                </button>
            )}
        </div>
    </div>
);

const PricingEntryCards = ({ entries }) => {
    if (entries.length === 0) {
        return (
            <div className="py-14 text-center">
                <FiDollarSign className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-500">No pricing records found</p>
            </div>
        );
    }

    return (
        <div className="divide-y divide-gray-200">
            {entries.map(entry => {
                const values = getEntryValues(entry);

                return (
                    <div key={entry.id} className="p-4 sm:p-5">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className={`px-2 py-0.5 rounded border text-[10px] font-medium uppercase tracking-wide ${entry.type === 'estimation'
                                    ? 'border-purple-200 bg-purple-50 text-purple-700'
                                    : 'border-blue-200 bg-blue-50 text-blue-700'
                                    }`}>
                                    {entry.type}
                                </span>
                                {entry.department && (
                                    <span className="px-2 py-0.5 rounded border border-gray-200 text-[10px] font-medium bg-gray-50 text-gray-600 capitalize">
                                        {entry.department.replace(/_/g, ' ')}
                                    </span>
                                )}
                                <span className="text-xs text-gray-500">Unit {entry.unit_number}</span>
                                <span className="text-xs text-gray-400">Revision {entry.quote_revision || 1}</span>
                            </div>
                            <span className="text-[11px] text-gray-400">
                                {entry.updated_by_name || entry.created_by_name || 'Unknown user'}
                            </span>
                        </div>

                        {values.length === 0 ? (
                            <p className="text-xs text-gray-400">This record has no entered monetary values.</p>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {values.map(item => (
                                    <div key={item.key} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                                        <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium truncate">
                                            {item.label}
                                        </p>
                                        <p className="text-sm font-semibold text-gray-800 mt-0.5 tabular-nums">
                                            {formatMoney(item.value)}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const DepartmentSummaryCard = ({ department, estimationAmount, departmentAmount, quotingAmount }) => {
    const DepartmentIcon = DEPARTMENT_ICONS[department.key];

    return (
        <div className="flex-1 min-w-0 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-100 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-gray-50 text-gray-500">
                    <DepartmentIcon className="w-4 h-4" />
                </div>
                <h2 className="text-sm sm:text-base font-semibold text-gray-900">{department.label} Summary</h2>
            </div>
            <div className="p-4 sm:p-5 grid grid-cols-3 gap-4">
                <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-1">Estimated Total</p>
                    <p className="text-xl font-bold text-gray-500 tabular-nums">{formatMoney(estimationAmount)}</p>
                </div>
                <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-1">Reviewed Total</p>
                    <p className="text-xl font-bold text-gray-900 tabular-nums">{formatMoney(departmentAmount)}</p>
                </div>
                <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-emerald-600 mb-1">Quotable Total</p>
                    <p className="text-xl font-bold text-emerald-700 tabular-nums">{formatMoney(quotingAmount)}</p>
                </div>
            </div>
            <p className="text-xs text-gray-400 px-4 pb-4 sm:px-5 sm:pb-5 -mt-2">Estimated is the estimator's rough figure; Reviewed is the designer's number where entered; Quotable is what would currently be sent to the customer.</p>
        </div>
    );
};

const FinalPricingDetails = ({
    modalJobId,
    modalCompanySlug,
    onClose,
    onSaved,
} = {}) => {
    const { company, userProfile } = useAuth();

    const {
        companySlug: paramsCompanySlug,
        jobId: paramsJobId,
    } = useParams();

    const navigate = useNavigate();

    const isModal = typeof onClose === 'function';

    const jobId = modalJobId ?? paramsJobId;
    const companySlug = modalCompanySlug ?? paramsCompanySlug;

    const canView = hasExactPermission(userProfile, VIEW_PERMISSION);
    const canEdit = hasExactPermission(userProfile, EDIT_PERMISSION);

    const requireFinalPricing = isFinalPricingRequired(company);

    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(false);
    const createEmptyDepartmentState = () => ({
        roof: {
            finalPrice: '',
            notes: '',
        },
        wall: {
            finalPrice: '',
            notes: '',
        },
        floor: {
            finalPrice: '',
            notes: '',
        },
    });

    const [departmentValues, setDepartmentValues] = useState(
        createEmptyDepartmentState
    );

    const [savingDepartment, setSavingDepartment] =
        useState(null);

    const saving = savingDepartment !== null;


    const loadJob = useCallback(async () => {
        if (!canView || !company?.id || !jobId) return;

        try {
            setLoading(true);
            const data = await getFinalPricingJob(company.id, jobId);
            setJob(data);
            setDepartmentValues({
                roof: {
                    finalPrice:
                        data.final_pricings?.roof?.final_price !== null &&
                            data.final_pricings?.roof?.final_price !== undefined
                            ? String(data.final_pricings.roof.final_price)
                            : '',
                    notes:
                        data.final_pricings?.roof?.notes || '',
                },

                wall: {
                    finalPrice:
                        data.final_pricings?.wall?.final_price !== null &&
                            data.final_pricings?.wall?.final_price !== undefined
                            ? String(data.final_pricings.wall.final_price)
                            : '',
                    notes:
                        data.final_pricings?.wall?.notes || '',
                },

                floor: {
                    finalPrice:
                        data.final_pricings?.floor?.final_price !== null &&
                            data.final_pricings?.floor?.final_price !== undefined
                            ? String(data.final_pricings.floor.final_price)
                            : '',
                    notes:
                        data.final_pricings?.floor?.notes || '',
                },
            });
        } catch (error) {
            console.error('Failed to load final pricing job:', error);
            Toast.error('Failed to load job pricing', { position: 'bottom-right' });
            if (isModal) {
                onClose?.();
            } else {
                navigate(`/${companySlug}/final-pricings`);
            }
        } finally {
            setLoading(false);
        }
    }, [
        canView,
        company?.id,
        companySlug,
        isModal,
        jobId,
        navigate,
        onClose,
    ]);


    useEffect(() => {
        loadJob();
    }, [loadJob]);

    useEffect(() => {
        if (!isModal) return undefined;

        const previousOverflow = document.body.style.overflow;

        document.body.style.overflow = 'hidden';

        const handleKeyDown = event => {
            if (event.key === 'Escape' && !saving) {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isModal, onClose, saving]);

    const activeEntries = useMemo(() => {
        return (job?.pricing_entries || [])
            .filter(entry => !entry.is_archived)
            .sort((a, b) => {
                const revisionDiff = Number(b.quote_revision || 1) - Number(a.quote_revision || 1);
                if (revisionDiff !== 0) return revisionDiff;

                const unitDiff = Number(a.unit_number || 1) - Number(b.unit_number || 1);
                if (unitDiff !== 0) return unitDiff;

                return new Date(a.created_at) - new Date(b.created_at);
            });
    }, [job?.pricing_entries]);

    const estimationTotals = useMemo(
        () => computeEstimationTotals(job?.pricing_entries),
        [job?.pricing_entries]
    );

    const updateDepartmentValue = (
        department,
        field,
        value
    ) => {
        setDepartmentValues(current => ({
            ...current,

            [department]: {
                ...current[department],
                [field]: value,
            },
        }));
    };

    const save = async department => {
        if (
            !canEdit ||
            savingDepartment
        ) {
            return;
        }

        const departmentState =
            departmentValues[department];

        const amount = parseMoney(
            departmentState?.finalPrice
        );

        const departmentLabel =
            FINAL_PRICING_DEPARTMENTS.find(
                item => item.key === department
            )?.label || department;

        if (
            amount === null ||
            amount < 0
        ) {
            Toast.error(
                `Enter a valid ${departmentLabel} final price`,
                {
                    position: 'bottom-right',
                }
            );

            return;
        }

        // Captured before the save so the ready-for-quotation notification
        // only fires the first time this department's final price is
        // entered — not on every subsequent edit. Existence (not amount) is
        // the right check: a legitimately-entered $0 final price must not
        // be confused with "not entered", matching the pattern already used
        // elsewhere in this codebase.
        const isFirstEntry = !job.final_pricings?.[department];

        try {
            setSavingDepartment(department);

            const pricing = await saveFinalPricing({
                companyId: company.id,
                jobId: job.id,
                department,
                finalPrice: amount,
                notes: departmentState.notes,
                userProfile,
            });

            setJob(current => ({
                ...current,

                final_pricings: {
                    ...(current.final_pricings || {}),
                    [department]: pricing,
                },
            }));

            setDepartmentValues(current => ({
                ...current,

                [department]: {
                    finalPrice: String(pricing.final_price),
                    notes: pricing.notes || '',
                },
            }));

            if (isFirstEntry) {
                const notifyUserIds = company?.settings?.pricing_settings?.final_pricing_entered_notify_user_ids || [];

                notifyReadyForQuotation({
                    job,
                    department,
                    departmentLabel,
                    finalPrice: pricing.final_price,
                    notes: pricing.notes,
                    company,
                    companySlug,
                    userIds: notifyUserIds,
                }).catch(err => console.error('Failed to send ready-for-quotation notification:', err));
            }

            onSaved?.({
                jobId: job.id,
                department,
                finalPricing: pricing,
            });

            Toast.success(
                `${departmentLabel} final pricing saved`,
                {
                    position: 'bottom-right',
                }
            );
        } catch (error) {
            console.error(
                `Failed to save ${department} final pricing:`,
                error
            );

            Toast.error(
                error.message ||
                `Failed to save ${departmentLabel} final pricing`,
                {
                    position: 'bottom-right',
                }
            );
        } finally {
            setSavingDepartment(null);
        }
    };

    const entriesByDepartment = useMemo(() => {
        return {
            roof: activeEntries.filter(entry =>
                entry.department === 'roof' ||
                (
                    entry.type === 'estimation' &&
                    (
                        entry.roof !== null ||
                        entry.labour !== null ||
                        entry.approved_roof !== null
                    )
                )
            ),

            wall: activeEntries.filter(entry =>
                entry.department === 'wall' ||
                (
                    entry.type === 'estimation' &&
                    (
                        entry.gf_wall !== null ||
                        entry.first_floor_wall !== null ||
                        entry.second_floor_wall !== null ||
                        entry.approved_wall !== null
                    )
                )
            ),

            floor: activeEntries.filter(entry =>
                entry.department === 'floor' ||
                (
                    entry.type === 'estimation' &&
                    (
                        entry.first_floor !== null ||
                        entry.second_floor !== null ||
                        entry.approved_floor !== null
                    )
                )
            ),
        };
    }, [activeEntries]);

    if (!canView) {
        const deniedContent = (
            <PermissionDenied
                isModal={isModal}
                onClose={onClose}
            />
        );

        if (!isModal) return deniedContent;

        return (
            <FinalPricingModalShell
                onClose={onClose}
                saving={false}
            >
                {deniedContent}
            </FinalPricingModalShell>
        );
    }

    if (loading || !job) {
        const loadingContent = (
            <div
                className={
                    isModal
                        ? 'min-h-[500px] flex items-center justify-center'
                        : 'min-h-[55vh] flex items-center justify-center'
                }
            >
                <div className="w-9 h-9 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />

                <span className="ml-3 text-sm text-gray-500">
                    Loading pricing…
                </span>
            </div>
        );

        if (!isModal) return loadingContent;

        return (
            <FinalPricingModalShell
                onClose={onClose}
                saving={saving}
            >
                {loadingContent}
            </FinalPricingModalShell>
        );
    }

    const pageContent = (
        <div
            className={
                isModal
                    ? 'flex flex-col gap-4 sm:gap-6'
                    : 'mobile-detail-page standalone-pricing-page flex flex-col gap-4 sm:gap-6 pb-10'
            }
        >
            <div className="flex flex-col gap-3">
                {!isModal && (
                    <BackButton
                        text="Back to Final Pricings"
                        variant="ghost"
                        size="sm"
                        iconType="chevron"
                        className="text-blue-600 hover:text-blue-800 self-start"
                    />
                )}

                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
                            <FiDollarSign className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 className="text-lg sm:text-2xl font-semibold text-gray-900 tracking-tight truncate">{job.unique_id}</h1>
                                <span className={`px-2 py-0.5 rounded-full border text-[10px] font-medium uppercase tracking-wide shrink-0 ${
                                    requireFinalPricing
                                        ? 'border-blue-200 bg-blue-50 text-blue-700'
                                        : 'border-gray-200 bg-gray-50 text-gray-600'
                                }`}>
                                    {requireFinalPricing ? 'Final pricing required' : 'Final pricing optional'}
                                </span>
                                {job.is_cancelled && (
                                    <span className="px-1.5 py-0.5 rounded border border-red-200 bg-red-50 text-red-600 text-[10px] font-medium uppercase tracking-wide shrink-0">Cancelled</span>
                                )}
                            </div>
                            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 truncate">
                                {job.customer_name || 'No customer'} · {addressToText(job.address)}
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => window.open(`/${companySlug}/assignment/${job.id}`, '_blank')}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 shrink-0"
                    >
                        <FiExternalLink className="w-4 h-4" />
                        Open assignment
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-4 sm:gap-6 items-start">
                <div className="flex flex-col gap-4 sm:gap-6">
                    <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
                        {FINAL_PRICING_DEPARTMENTS.map(department => (
                            <DepartmentSummaryCard
                                key={department.key}
                                department={department}
                                estimationAmount={estimationTotals.estimation.byDepartment[department.key]}
                                departmentAmount={estimationTotals.department.byDepartment[department.key]}
                                quotingAmount={estimationTotals.quoting.byDepartment[department.key]}
                            />
                        ))}
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <div className="px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 bg-gray-50">
                            <h2 className="text-sm sm:text-base font-semibold text-gray-800">Existing Pricing Records</h2>
                            <p className="text-xs text-gray-500 mt-1">
                                Estimator and department records are displayed as read-only reference values.
                            </p>
                        </div>

                        <PricingEntryCards entries={activeEntries} />
                    </div>
                </div>

                <div className="space-y-4 xl:sticky xl:top-4">
                    {!canEdit && (
                        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-700">
                            View-only access. You cannot change final
                            pricing.
                        </div>
                    )}

                    {FINAL_PRICING_DEPARTMENTS.map(department => {
                        const departmentKey = department.key;

                        const savedPricing =
                            job.final_pricings?.[departmentKey];

                        const formValue =
                            departmentValues[departmentKey];

                        const isSaving =
                            savingDepartment === departmentKey;

                        // Department color is used sparingly — as an icon chip, a thin
                        // left accent stripe, and the input focus ring — rather than a
                        // full-color header block. The Save action stays one consistent
                        // brand color across all three cards instead of three separate
                        // saturated button colors.
                        const cardStyles = {
                            roof: {
                                accent: 'border-l-orange-400',
                                chip: 'bg-orange-50 text-orange-600',
                                ring: 'focus:ring-orange-300 focus:border-orange-400',
                            },

                            wall: {
                                accent: 'border-l-blue-400',
                                chip: 'bg-blue-50 text-blue-600',
                                ring: 'focus:ring-blue-300 focus:border-blue-400',
                            },

                            floor: {
                                accent: 'border-l-emerald-400',
                                chip: 'bg-emerald-50 text-emerald-600',
                                ring: 'focus:ring-emerald-300 focus:border-emerald-400',
                            },
                        };

                        const style =
                            cardStyles[departmentKey];

                        return (
                            <div
                                key={departmentKey}
                                className={`rounded-xl border border-gray-200 border-l-4 ${style.accent} bg-white shadow-sm overflow-hidden`}
                            >
                                <div className="px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-100">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            {(() => {
                                                const DepartmentIcon = DEPARTMENT_ICONS[departmentKey];
                                                return (
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${style.chip}`}>
                                                        <DepartmentIcon className="w-4 h-4" />
                                                    </div>
                                                );
                                            })()}

                                            <div className="min-w-0">
                                                <h2 className="text-sm sm:text-base font-semibold text-gray-900">
                                                    {department.label} Final Pricing
                                                </h2>

                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    Final price for the{' '}
                                                    {department.label.toLowerCase()}{' '}
                                                    department.
                                                </p>
                                            </div>
                                        </div>

                                        {savedPricing && (
                                            <span className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px] font-medium uppercase tracking-wide">
                                                <FiCheckCircle className="w-3 h-3" />
                                                Saved
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="p-4 sm:p-5 space-y-4">
                                    <div>
                                        <label className="block text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-1.5">
                                            Final price (AUD)
                                        </label>

                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">
                                                $
                                            </span>

                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                value={
                                                    formValue?.finalPrice || ''
                                                }
                                                onChange={event =>
                                                    updateDepartmentValue(
                                                        departmentKey,
                                                        'finalPrice',
                                                        event.target.value
                                                    )
                                                }
                                                disabled={
                                                    !canEdit ||
                                                    saving
                                                }
                                                placeholder="0.00"
                                                className={`w-full h-11 pl-8 pr-3 rounded-lg border border-gray-300 text-lg font-semibold text-gray-900 tabular-nums focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:text-gray-600 ${style.ring}`}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-1.5">
                                            Notes
                                        </label>

                                        <textarea
                                            value={
                                                formValue?.notes || ''
                                            }
                                            onChange={event =>
                                                updateDepartmentValue(
                                                    departmentKey,
                                                    'notes',
                                                    event.target.value
                                                )
                                            }
                                            disabled={
                                                !canEdit ||
                                                saving
                                            }
                                            rows={3}
                                            placeholder={`Optional notes about the ${department.label.toLowerCase()} final pricing…`}
                                            className={`w-full rounded-lg border border-gray-300 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:text-gray-600 ${style.ring}`}
                                        />
                                    </div>

                                    {savedPricing && (
                                        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs text-gray-500 space-y-1">
                                            <p>
                                                Last updated by{' '}
                                                <span className="font-medium text-gray-700">
                                                    {savedPricing.updated_by_name ||
                                                        savedPricing.created_by_name ||
                                                        'Unknown user'}
                                                </span>
                                            </p>

                                            {savedPricing.updated_at && (
                                                <p>
                                                    {new Date(
                                                        savedPricing.updated_at
                                                    ).toLocaleString('en-AU')}
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {canEdit && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                save(departmentKey)
                                            }
                                            disabled={saving}
                                            className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <FiSave className="w-4 h-4" />

                                            {isSaving
                                                ? 'Saving…'
                                                : savedPricing
                                                    ? `Update ${department.label} pricing`
                                                    : `Save ${department.label} pricing`}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    if (isModal) {
        return (
            <FinalPricingModalShell
                onClose={onClose}
                saving={saving}
            >
                {pageContent}
            </FinalPricingModalShell>
        );
    }

    return pageContent;
};

const FinalPricingModalShell = ({
    children,
    onClose,
    saving = false,
}) => {
    const handleBackdropClick = () => {
        if (!saving) {
            onClose?.();
        }
    };

    return (
        <div className="mobile-fullscreen-dialog pricing-details-modal fixed inset-0 z-[220] !mt-0">
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
                onClick={handleBackdropClick}
            />

            <div className="relative z-10 flex h-full w-full items-center justify-center px-0 py-0 sm:px-6 sm:py-5 lg:px-10 xl:px-14">
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Final pricing details"
                    className="
                        relative
                        flex
                        h-full
                        w-full
                        max-w-[1700px]
                        flex-col
                        overflow-hidden
                        rounded-none
                        sm:rounded-2xl
                        border-0
                        sm:border
                        border-gray-200
                        bg-gray-50
                        shadow-2xl
                    "
                    onClick={event => event.stopPropagation()}
                >
                    <div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 sm:px-6">
                        <div className="flex items-center gap-2 min-w-0">
                            <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                                <FiDollarSign className="w-4 h-4" />
                            </div>

                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900">
                                    Final Pricing
                                </p>

                                <p className="text-[11px] text-gray-500">
                                    Review Roof, Wall and Floor final pricing
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => {
                                if (!saving) {
                                    onClose?.();
                                }
                            }}
                            disabled={saving}
                            aria-label="Close final pricing"
                            className="
                                inline-flex
                                h-9
                                w-9
                                items-center
                                justify-center
                                rounded-lg
                                border
                                border-gray-200
                                bg-white
                                text-gray-500
                                transition
                                hover:bg-gray-100
                                hover:text-gray-800
                                disabled:cursor-not-allowed
                                disabled:opacity-50
                            "
                        >
                            <FiX className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                        <div className="mx-auto w-full px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
                            {children}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinalPricingDetails;
